import { DataGrid, DataGridColumn, DataGridRef } from '@cimplico/components';
import { Ref, useState } from 'react';
import { ROW_SIZES } from '../../constants';
import { useFirmInvitations } from '../../queries/use-firm-invitations';
import { Invitation } from '../../types/invitation';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import InvitationRow from './InvitationRow';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';

const columns: DataGridColumn[] = [
    {
        header: 'Email',
        alignment: 'left',
        width: '30%',
    },
    {
        header: 'Role',
        alignment: 'center',
        width: '10%',
    },
    {
        header: 'Created by',
        alignment: 'left',
        width: '20%',
    },
    {
        header: 'Status',
        alignment: 'center',
        width: '10%',
    },
    {
        header: 'Expires',
        alignment: 'left',
        width: '20%',
    },
];

type InvitationsTableProps = {
    search?: string;
    sort?: string;
    filter?: string;
    onSelect?: (invitation: Invitation[]) => void;
    isSelectable?: boolean;
    tableRef: Ref<DataGridRef> | null;
};

function InvitationsTable({ onSelect, isSelectable, tableRef, search, sort, filter }: InvitationsTableProps): JSX.Element {
    const { isAdmin, isManager } = useIsFirmRole();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(ROW_SIZES[0]);

    const roleCanInvite = isAdmin || isManager;

    const invitationsQuery = useFirmInvitations({ page, limit, search, sort, filter });

    function selectInvitations(ids: string[]): void {
        const selectedInvitations = invitations.filter((i) => ids.includes(i.id)) ?? [];
        onSelect && onSelect(selectedInvitations);
    }

    if (invitationsQuery.isLoading) {
        return <></>;
    }
    // TODO: Create an error widget
    if (invitationsQuery.isError || !invitationsQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const { invitations, totalRows } = invitationsQuery.data;

    return (
        <div className="w-full">
            <DataGrid
                ref={tableRef}
                columns={columns}
                rowSizes={ROW_SIZES}
                totalRows={totalRows}
                onPageChange={setPage}
                onRowSizeChange={setLimit}
                onSelect={selectInvitations}
                isLoading={invitationsQuery.isFetching}
                isSelectable={isSelectable}
                emptyMessage={
                    <EmptyStateNew
                        icon={<UserPlusIcon />}
                        navigateTo="/users/new"
                        title="Invite your users"
                        disableActionButtonHint={!roleCanInvite ? 'This requires an admin or manager role.' : undefined}
                        disableActionButton={!roleCanInvite}
                        actionText="New User"
                        helpLink="https://learn.cimplico.com/docs/manage-users#inviting-users"
                        helpText="Invite team members into Cimplico Workpapers."
                    />
                }
            >
                {invitations.map((invitation) => {
                    return <InvitationRow id={invitation.id} key={invitation.id} invitation={invitation} />;
                })}
            </DataGrid>
        </div>
    );
}

export default InvitationsTable;
